import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Quote, Star, ArrowLeft, ArrowRight } from "lucide-react";
import api from "../lib/api";
import { Reveal } from "../components/Reveal";

export default function Testimonials() {
  const [items, setItems] = useState([]);
  const [brands, setBrands] = useState([]);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    api.get("/testimonials").then((r) => setItems(r.data)).catch(() => {});
    api.get("/brands").then((r) => setBrands(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (items.length < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % items.length), 7000);
    return () => clearInterval(t);
  }, [items]);

  const current = items[idx];

  return (
    <section id="testimonials" className="py-28 bg-white border-b border-[#E5E5E5] overflow-hidden" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <Reveal className="lg:col-span-4">
          <span className="font-mono text-[11px] tracking-[0.25em] uppercase text-[#6B1414]">05 / Sellers on Calium</span>
          <h2 className="mt-4 font-heading text-4xl sm:text-5xl font-black tracking-[-0.03em] text-[#0A0A0A] leading-[0.95]">
            Words from the people we sell for.
          </h2>
          <div className="mt-10 flex items-center gap-3">
            <button data-testid="testimonial-prev" onClick={() => setIdx((idx - 1 + items.length) % items.length)} className="w-11 h-11 rounded-full border border-[#0A0A0A] flex items-center justify-center hover:bg-[#0A0A0A] hover:text-white transition-colors" aria-label="Previous testimonial"><ArrowLeft size={16} /></button>
            <button data-testid="testimonial-next" onClick={() => setIdx((idx + 1) % items.length)} className="w-11 h-11 rounded-full border border-[#0A0A0A] flex items-center justify-center hover:bg-[#0A0A0A] hover:text-white transition-colors" aria-label="Next testimonial"><ArrowRight size={16} /></button>
            <span className="font-mono text-xs text-gray-400 ml-2">{items.length ? `${idx + 1} / ${items.length}` : ""}</span>
          </div>
        </Reveal>

        <div className="lg:col-span-8 relative min-h-[320px]">
          <Quote strokeWidth={1} className="absolute -top-4 -left-2 sm:-top-6 sm:-left-4 text-[#F5D5B0] w-12 h-12 sm:w-[120px] sm:h-[120px]" aria-hidden="true" />
          <AnimatePresence mode="wait">
            {current && (
              <motion.figure
                key={current.id}
                data-testid="testimonial-card"
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -24 }}
                transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                className="relative pl-2 sm:pl-16 pt-12 sm:pt-10"
              >
                <div className="flex gap-1 mb-6 text-[#C8956A]">
                  {Array.from({ length: current.rating || 5 }).map((_, i) => <Star key={i} size={14} fill="currentColor" />)}
                </div>
                <blockquote className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#0A0A0A] leading-[1.15]">
                  &ldquo;{current.quote}&rdquo;
                </blockquote>
                <figcaption className="mt-8 flex items-center gap-4">
                  <span className="w-12 h-12 rounded-full bg-[#6B1414] text-[#F5D5B0] font-heading font-black flex items-center justify-center">{current.name?.[0]}</span>
                  <div>
                    <p className="font-body font-bold text-[#0A0A0A]">{current.name}</p>
                    <p className="font-mono text-[10px] uppercase tracking-wider text-gray-500">{current.role}{current.role && " · "}{current.company} · {current.marketplace}</p>
                  </div>
                </figcaption>
              </motion.figure>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* brands marquee */}
      <div className="mt-24 border-y border-[#E5E5E5] py-6 select-none" data-testid="brands-marquee">
        <p className="text-center font-mono text-[10px] uppercase tracking-[0.3em] text-gray-400 mb-5">Brands we operate for</p>
        <div className="overflow-hidden">
          <div className="animate-drift-slow flex whitespace-nowrap gap-14">
            {[...brands, ...brands].map((b, i) => (
              <span key={i} className="font-heading text-2xl sm:text-3xl font-black tracking-tight text-[#0A0A0A]/20 hover:text-[#6B1414] transition-colors">{b}</span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
